import React, { useRef } from 'react';
import { cx } from './classNames';
import './ui-layout.css';

export interface TabItem {
  key: string;
  label: React.ReactNode;
  /** 右肩の件数ピル。0 も出す（「無い」ことが情報なので） */
  count?: number;
  icon?: React.ReactNode;
  /** 押せない理由。指定すると disabled になり title で理由が出る（原則 8） */
  disabledReason?: string;
  /** 対応するパネルの id。渡すと aria-controls を張る */
  panelId?: string;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (key: string) => void;
  /** tablist の読み上げ名（「マスタ管理の切り替え」など） */
  label: string;
  className?: string;
}

/**
 * ページ内タブ（4 パターンのひとつ）。URL には依存しない制御コンポーネント。
 * Phase 2 でネストルートに載せるときは、呼び出し側が value/onChange を
 * ルータと繋ぐ。
 */
export function Tabs({ items, value, onChange, label, className }: TabsProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectable = items.filter((item) => !item.disabledReason);

  const moveTo = (index: number) => {
    const target = selectable[(index + selectable.length) % selectable.length];
    if (!target) return;
    const position = items.findIndex((item) => item.key === target.key);
    refs.current[position]?.focus();
    onChange(target.key);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const current = selectable.findIndex((item) => item.key === value);
    if (current < 0) return;

    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        moveTo(current + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveTo(current - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        break;
      case 'End':
        event.preventDefault();
        moveTo(selectable.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className={cx('ui-tabs', className)} role="tablist" aria-label={label}>
      {items.map((item, index) => {
        const active = item.key === value;
        const disabled = Boolean(item.disabledReason);

        return (
          <button
            key={item.key}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`ui-tab-${item.key}`}
            className={cx('ui-tab', active && 'ui-tab--active')}
            aria-selected={active}
            aria-controls={item.panelId}
            // 選択中のタブだけを Tab キーの止まり先にする（roving tabindex）
            tabIndex={active ? 0 : -1}
            disabled={disabled}
            title={item.disabledReason}
            onClick={() => onChange(item.key)}
            onKeyDown={handleKeyDown}
          >
            {item.icon && <span className="ui-tab__icon">{item.icon}</span>}
            <span className="ui-tab__label">{item.label}</span>
            {item.count != null && <span className="ui-tab__count">{item.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
